import { yupResolver } from '@hookform/resolvers/yup';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setNotice } from 'src/redux/noticesSlice';
import * as yup from 'yup';
import { BACKEND_URL } from '../../../constants';
import { handlePasteToForm, isObjectEmpty } from '../../../helpers';
import {
  IOauthParams,
  IProvider,
  ProviderType,
  useCreateProviderMutation,
  useUpdateAvatarMutation,
} from '../../../redux/services/provider';
import { CustomTypography } from '../../custom/CustomTypography';
import { PasswordTextfield } from '../../custom/PasswordTextfield';
import { ConfirmationModal } from '../../modal/ConfirmationModal';
import { SharedFormProvider } from '../../shared/FormProvider';
import { IconWithTooltip } from '../../shared/IconWithTooltip';
import { SidePanel } from '../../sidePanel/SidePanel';
import { ProviderHeader } from '../ProviderHeader';
import { ScopeChips } from '../ScopeChips';
import styles from './CreateProvider.module.css';
import { ENoticeType } from 'src/enums';

const schema = yup.object({
  name: yup
    .string()
    .max(50, 'Название не может превышать 50 символов')
    .required('Обязательное поле')
    .matches(/[^ ]+/, {
      message: 'Название не может состоять только из пробелов',
    })
    .matches(/^[^ ]+( *[^ ]+)*?$/, 'Название не может содержать пробелы в начале и конце'),
  description: yup
    .string()
    .max(255, 'Описание не может превышать 255 символов')
    .matches(/^$|[^ ]+/, {
      message: 'Описание не может состоять только из пробелов',
    }),
  password_required: yup.boolean(),
  is_public: yup.boolean(),
  params: yup.object({
    external_client_id: yup
      .string()
      .max(255, 'Идентификатор не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Идентификатор не может содержать пробелы',
      }),
    external_client_secret: yup
      .string()
      .max(255, 'Секретный ключ не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Секретный ключ не может содержать пробелы',
      }),
    authorization_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    token_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    issuer: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    userinfo_endpoint: yup
      .string()
      .url('Неверный формат ссылки')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .required('Обязательное поле'),
    mapping: yup
      .string()
      .max(2000, 'Длина выражения для сопоставления артибутов не может превышать 2000 символов'),
  }),
});

export type TChips = {
  key: string;
  value: string;
  changing?: boolean;
};

export interface ICreateProvider {
  isOpen: boolean;
  onClose: (createChooseProvider?: boolean) => void;
  defaultAvatar?: string;
}

export const CreateProvider: FC<ICreateProvider> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const methods = useForm<IProvider<IOauthParams>>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: ProviderType.CUSTOM,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    clearErrors,
    reset,
    control,
  } = methods;

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [chips, setChips] = useState<TChips[]>([]);
  const [createProvider, createResult] = useCreateProviderMutation();
  const [updateAvatar] = useUpdateAvatarMutation();

  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  useEffect(() => {
    if (createResult.isSuccess) onClose(true);
  }, [createResult]);

  useEffect(() => {
    return () => {
      reset();
      setChips([]);
    };
  }, [isOpen]);

  const closeSaveModal = () => setSaveModalOpen(false);

  const handleClose = () => {
    if (isObjectEmpty(dirtyFields)) onClose();
    else setSaveModalOpen(true);
  };

  const pasteFields = async () => {
    try {
      await handlePasteToForm(methods);
    } catch (err) {
      dispatch(
        setNotice({
          type: ENoticeType.error,
          id: Math.random(),
          isRead: false,
          message: `Ошибка вставки параметров: ${err}`,
          timestamp: new Date().toString(),
        }),
      );
    }
  };

  const onSubmit: SubmitHandler<IProvider<IOauthParams>> = (data) => {
    const scopes = chips.map((chip) => chip.value).join(' ');
    createProvider({
      body: {
        ...data,
        params: {
          ...data.params,
          ...(scopes && { scopes }),
        },
      } as IProvider<IOauthParams>,
      clientId: clientId || appId,
    })
      .unwrap()
      .then((provider) => {
        if (provider.id) {
          setTimeout(() => {
            updateAvatar({
              clientId: clientId || appId,
              providerId: provider.id,
              avatar: uploadedAvatar,
            }).unwrap();
          }, 1000);
        }
      });
  };

  return (
    <SidePanel
      onClose={handleClose}
      isOpen={isOpen}
      title="Создать способ входа"
      isNoBackdrop
      AdditionalAction={pasteFields}
      customAdditionalText="Вставить настройки"
      CustomAdditionalIcon={ContentPasteGoOutlinedIcon}
    >
      <SharedFormProvider<IProvider<IOauthParams>>
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        handleCancel={handleClose}
        isDisabled={createResult.isLoading}
        acceptTitle="Создать"
      >
        <div className={styles['create-provider-form']}>
          <ProviderHeader />
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Идентификатор ресурса (client_id)
          </CustomTypography>
          <TextField
            {...register('params.external_client_id', {
              required: true,
              onChange: () => {
                if (errors.params?.external_client_id) clearErrors('params.external_client_id');
              },
            })}
            className="custom"
            error={!!errors.params?.external_client_id}
            helperText={errors.params?.external_client_id?.message}
            fullWidth
            variant="standard"
            autoComplete="off"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Уникальный идентификатор подключаемого ресурса
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Секретный ключ (client_secret)
          </CustomTypography>
          <PasswordTextfield
            nameField="params.external_client_secret"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Секретный ключ ресурса
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles['input-title'])}>
            Возвратный URL(Redirect URI)
          </CustomTypography>
          <TextField
            value={BACKEND_URL + '/api/interaction/code'}
            disabled
            className="custom"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconWithTooltip
                    iconType="copy"
                    onClick={() =>
                      navigator.clipboard.writeText(BACKEND_URL + '/api/interaction/code')
                    }
                  />
                </InputAdornment>
              ),
            }}
            fullWidth
            variant="standard"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Cсылка должна быть указана в настройках внешних способов входа для корректной
            аутентификации пользователя
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Базовый адрес сервера авторизации (issuer)
          </CustomTypography>
          <TextField
            {...register('params.issuer', {
              required: true,
              onChange: () => {
                if (errors.params?.issuer) clearErrors('params.issuer');
              },
            })}
            style={{ marginBottom: 32 }}
            className="custom"
            error={!!errors.params?.issuer}
            helperText={errors.params?.issuer?.message}
            fullWidth
            variant="standard"
          />
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Адрес авторизации (authorization_endpoint)
          </CustomTypography>
          <TextField
            {...register('params.authorization_endpoint', {
              required: true,
              onChange: () => {
                if (errors.params?.authorization_endpoint)
                  clearErrors('params.authorization_endpoint');
              },
            })}
            className="custom"
            error={!!errors.params?.authorization_endpoint}
            helperText={errors.params?.authorization_endpoint?.message}
            fullWidth
            variant="standard"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Адрес, на который пользователь переадресовывается для авторизации
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Адрес выдачи токена (token_endpoint)
          </CustomTypography>
          <TextField
            {...register('params.token_endpoint', {
              required: true,
              onChange: () => {
                if (errors.params?.token_endpoint) clearErrors('params.token_endpoint');
              },
            })}
            className="custom"
            error={!!errors.params?.token_endpoint}
            helperText={errors.params?.token_endpoint?.message}
            fullWidth
            variant="standard"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Ресурс, обеспечивающий выдачу токенов
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Адрес получения информации о пользователе (userinfo_endpoint)
          </CustomTypography>
          <TextField
            {...register('params.userinfo_endpoint', {
              required: true,
              onChange: () => {
                if (errors.params?.userinfo_endpoint) clearErrors('params.userinfo_endpoint');
              },
            })}
            className="custom"
            error={!!errors.params?.userinfo_endpoint}
            helperText={errors.params?.userinfo_endpoint?.message}
            fullWidth
            variant="standard"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Ресурс, который возвращает информацию о текущем пользователе
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles['input-title'])}>
            Запрашиваемые разрешения
          </CustomTypography>
          <ScopeChips chips={chips} setChips={setChips} />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Перечень разрешений, которые должны быть получены при обращении к поставщику
            идентификации. Для добавления разрешения введите его имя и нажмите Enter.
          </CustomTypography>
        </div>
      </SharedFormProvider>

      <ConfirmationModal
        title="Сохранение изменений"
        mainMessage={['Изменения не сохранены. Продолжить без сохранения?']}
        actionButtonText="Продолжить"
        isOpen={saveModalOpen}
        onAction={() => {
          onClose();
          setSaveModalOpen(false);
        }}
        onClose={closeSaveModal}
      />
    </SidePanel>
  );
};
